import { useEffect, useRef, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { clearConditionalCache } from "../api/conditionalGet";
import { useRelease150 } from "./release150";
import {
  browserCodec,
  clearPersistedQueries,
  isPersistableKey,
  persistIdentity,
  restoreQueries,
  saveQueries,
  type PersistCodec
} from "./queryPersistence";

/** How long after the last fresh answer the kept copy is written (one write for a burst of answers). */
export const SAVE_DEBOUNCE_MS = 1000;

function sessionStore(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * Release 1.5.0 (perf, only when the 1.5.0 switch is on) — keeps the operational screens' last answers in this tab and
 * puts them back after a reload, for the same person with the same roles only (lib\queryPersistence.ts). When the
 * person signs out, a different person signs in or their roles change, every kept answer — in memory, in the tab and in
 * the conditional-request cache — is thrown away before anything else is shown. With the switch off it renders its
 * children and does nothing else.
 */
export function PersistQueryGate({
  children,
  storage = sessionStore(),
  codec = browserCodec()
}: {
  children: ReactNode;
  storage?: Storage | null;
  codec?: PersistCodec | null;
}) {
  const enabled = useRelease150();
  const { principal, persona, loading } = useAuth();
  const queryClient = useQueryClient();
  const { pathname } = useLocation();
  const identity = enabled && storage && codec ? persistIdentity(principal, persona, pathname) : null;
  const identityKey = identity ? JSON.stringify(identity) : null;
  // Who the in-memory answers belong to (owner + roles), so a role change is noticed even on the kiosk/present routes.
  const holderKey = principal ? `${principal.userPrincipalName ?? persona ?? ""}|${[...principal.roles].sort().join(",")}` : null;
  const lastHolder = useRef<string | null>(null);
  const restoredFor = useRef<string | null>(null);

  // Sign-out, another person or a role change: forget everything kept before.
  useEffect(() => {
    if (!enabled || loading) return;
    const previous = lastHolder.current;
    lastHolder.current = holderKey;
    if (!holderKey) {
      if (storage) clearPersistedQueries(storage);
      clearConditionalCache();
      restoredFor.current = null;
      return;
    }
    if (previous && previous !== holderKey) {
      queryClient.clear();
      clearConditionalCache();
      if (storage) clearPersistedQueries(storage);
      restoredFor.current = null;
    }
  }, [enabled, loading, holderKey, queryClient, storage]);

  // Put the kept answers back once per identity (the screens refetch them in the background: stale-while-revalidate).
  useEffect(() => {
    if (!identity || !identityKey || !storage || !codec || restoredFor.current === identityKey) return;
    restoredFor.current = identityKey;
    void restoreQueries(queryClient, identity, storage, codec);
    // identity is derived from identityKey
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identityKey, queryClient, storage, codec]);

  // Keep fresh answers, a second after the last one arrives.
  useEffect(() => {
    if (!identity || !storage || !codec) return;
    let timer: number | undefined;
    const save = () => {
      timer = undefined;
      void saveQueries(queryClient, identity, storage, codec);
    };
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== "updated" || event.action.type !== "success") return;
      if (!isPersistableKey(event.query.queryKey)) return;
      if (timer === undefined) timer = window.setTimeout(save, SAVE_DEBOUNCE_MS);
    });
    return () => {
      unsubscribe();
      if (timer !== undefined) window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identityKey, queryClient, storage, codec]);

  return <>{children}</>;
}
